import FVector from '@data/geometry/FVector';
import { computeOutwardOffsetPointsForEdge } from '@rendering/DesignSchemes/DesignSchemeUtils';
import * as THREE from 'three';
import { Component, FacadeComponentContext } from './Component';

export type GreenConfig = {
    depth: number;
};

export class GreenComponent extends Component {
    private readonly depth: number;
    private readonly perimeter: FVector[];
    private readonly requireActiveSensors: boolean;

    constructor(context: FacadeComponentContext, config: GreenConfig) {
        super(context);
        this.depth = THREE.MathUtils.clamp(config.depth, 0.1, 4);
        this.perimeter = context.perimeter;
        this.requireActiveSensors = context.requireActiveSensors;
    }

    override build(): THREE.Group {
        const group = new THREE.Group();

        // If required, only render if at least one sensor is active in the facade:
        if (this.requireActiveSensors && !this.sensors.some((sensor) => sensor.getScalarValue() > 0)) return group;

        const [p1Offset2D, p2Offset2D] = computeOutwardOffsetPointsForEdge(this.perimeter, this.p1, this.p2, this.depth);
        if (p1Offset2D.distanceToSquared(p2Offset2D) <= 1e-8) return group;
        const p1Offset = new FVector(p1Offset2D.x, p1Offset2D.y, this.p1.z);
        const p2Offset = new FVector(p2Offset2D.x, p2Offset2D.y, this.p2.z);

        const topP1Offset = new FVector(p1Offset.x, p1Offset.y, p1Offset.z + this.height);
        const topP2Offset = new FVector(p2Offset.x, p2Offset.y, p2Offset.z + this.height);

        // Floating panel parallel to the facade, offset by depth.
        const points = [topP1Offset, p1Offset, topP2Offset, p2Offset];
        const geom = new THREE.BufferGeometry().setFromPoints(points.map((p) => new THREE.Vector3(p.x, p.y, p.z)));
        geom.setIndex([0, 1, 2, 2, 1, 3]);
        geom.computeVertexNormals();

        const mesh = new THREE.Mesh(geom, this.material);
        const edges = new THREE.LineSegments(new THREE.EdgesGeometry(geom, 0.01), GreenComponent.edgeMaterial);
        group.add(mesh, edges);

        return group;
    }
}
